/**
 * BillingPage — the org's plan, usage meters, and upgrade buttons. Owner
 * only (the sidebar hides it from guests, and the backend blocks guests from
 * checkout and the portal). Checkout is the Stripe embedded form in a
 * dialog (EmbeddedCheckout). The Customer Portal is hosted by Stripe.
 */
import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { PRODUCT_BRAND } from '../../utils/productBrand';
import EmbeddedCheckout from './EmbeddedCheckout';
import {
  Badge,
  Box,
  Button,
  Callout,
  Card,
  Flex,
  Grid,
  Heading,
  Progress,
  Spinner,
  Text,
} from '@radix-ui/themes';
import {
  FEATURE_LABELS,
  LIMIT_LABELS,
  openPortal,
  startCheckout,
  startCreditPackCheckout,
  usageLabel,
  usagePercent,
  useBilling,
  type LimitKey,
  type PlanSummary,
} from '../../utils/billing';

const STATUS_LABELS: Record<string, { label: string; color: 'green' | 'amber' | 'red' | 'gray' | 'blue' }> = {
  active: { label: 'Active', color: 'green' },
  trialing: { label: 'Trial', color: 'blue' },
  past_due: { label: 'Payment due', color: 'red' },
  canceled: { label: 'Canceled', color: 'gray' },
  trial_expired: { label: 'Trial ended', color: 'amber' },
  comped: { label: 'Complimentary', color: 'green' },
  comp_expired: { label: 'Comp ended', color: 'amber' },
  suspended: { label: 'Suspended', color: 'red' },
};

export function PlanBadge({ name, status }: { name: string; status: string | null }) {
  const s = status ? STATUS_LABELS[status] : undefined;
  return (
    <Flex gap="2" align="center">
      <Badge size="2" variant="solid">{name}</Badge>
      {s && <Badge size="2" color={s.color} variant="soft">{s.label}</Badge>}
    </Flex>
  );
}

function limitText(limit: number | null, noun: string): string {
  return limit === null ? `Unlimited ${noun}` : `${limit} ${noun}`;
}

const BillingPage: React.FC = () => {
  const { billing, loading } = useBilling();
  const [params] = useSearchParams();
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [checkoutDialog, setCheckoutDialog] = useState<{ title: string; load: () => Promise<string> } | null>(null);

  const go = async (key: string, fn: () => Promise<string>) => {
    setBusy(key);
    setError(null);
    try {
      window.location.assign(await fn());
    } catch (err) {
      const e = err as { response?: { data?: { message?: string } } };
      setError(e.response?.data?.message || 'Could not open billing. Try again.');
      setBusy(null);
    }
  };

  if (loading) {
    return (
      <Flex align="center" gap="2"><Spinner /><Text color="gray">Loading plan…</Text></Flex>
    );
  }
  if (!billing) {
    return (
      <Callout.Root color="red"><Callout.Text>Could not load your plan.</Callout.Text></Callout.Root>
    );
  }

  const checkout = params.get('checkout');
  const limitKeys = Object.keys(billing.limits) as LimitKey[];

  return (
    <Flex direction="column" gap="4">
      <Flex justify="between" align="start" gap="3" wrap="wrap">
        <Box>
          <Heading size="6">Billing</Heading>
          <Text size="2" color="gray">Your plan, what you use, and upgrades</Text>
        </Box>
        <PlanBadge name={billing.plan.name} status={billing.plan.status} />
      </Flex>

      {!billing.billingEnabled && (
        <Callout.Root color="gray">
          <Callout.Text>
            Billing is not turned on. Every feature is on, with no limits.
          </Callout.Text>
        </Callout.Root>
      )}
      {checkout === 'success' && (
        <Callout.Root color="green">
          <Callout.Text>Thanks! Your plan updates in a few seconds. Refresh if it has not changed.</Callout.Text>
        </Callout.Root>
      )}
      {checkout === 'credits' && (
        <Callout.Root color="green">
          <Callout.Text>Thanks! Your AI credits arrive in a few seconds. Refresh if they have not.</Callout.Text>
        </Callout.Root>
      )}
      {checkout === 'canceled' && (
        <Callout.Root color="gray">
          <Callout.Text>Checkout was canceled. Nothing changed.</Callout.Text>
        </Callout.Root>
      )}
      {billing.plan.status === 'trialing' && (
        <Callout.Root color="blue">
          <Callout.Text>
            Your trial of {billing.plan.name} ends in {billing.plan.trialDaysLeft}{' '}
            {billing.plan.trialDaysLeft === 1 ? 'day' : 'days'}. After that you move to Free. You keep all your data.
          </Callout.Text>
        </Callout.Root>
      )}
      {billing.plan.status === 'past_due' && (
        <Callout.Root color="red">
          <Callout.Text>
            Your last payment failed. Update your card to keep your plan. Your game nights keep working.
          </Callout.Text>
        </Callout.Root>
      )}
      {billing.plan.status === 'trial_expired' && (
        <Callout.Root color="amber">
          <Callout.Text>
            Your trial ended, so you are on Free. Your game nights and data are safe. Upgrade to add more.
          </Callout.Text>
        </Callout.Root>
      )}
      {billing.plan.status === 'suspended' && (
        <Callout.Root color="red">
          <Callout.Text>
            This organization is suspended, so it cannot create anything new. Running game nights still work. Contact support.
          </Callout.Text>
        </Callout.Root>
      )}
      {!billing.canManage && (
        <Callout.Root color="gray">
          <Callout.Text>Only an owner can change the plan.</Callout.Text>
        </Callout.Root>
      )}
      {error && (
        <Callout.Root color="red"><Callout.Text>{error}</Callout.Text></Callout.Root>
      )}

      <Card>
        <Flex direction="column" gap="3">
          <Heading size="3">Usage</Heading>
          {limitKeys.map((key) => {
            const tracked = billing.usage[key];
            const used = tracked ?? 0;
            const limit = billing.limits[key];
            const pct = usagePercent(used, limit);
            return (
              <Box key={key}>
                <Flex justify="between" mb="1">
                  <Text size="2">{LIMIT_LABELS[key] ?? key}</Text>
                  <Text size="2" color="gray">
                    {tracked === null ? `n/a${limit !== null ? ` (${limit} a month)` : ''}` : usageLabel(used, limit)}
                  </Text>
                </Flex>
                {limit !== null && tracked !== null && (
                  <Progress value={pct} color={pct >= 100 ? 'amber' : undefined} aria-label={`${LIMIT_LABELS[key]} used`} />
                )}
              </Box>
            );
          })}
          <Flex justify="between" align="center" gap="3" wrap="wrap">
            <Box>
              <Text as="div" size="2">AI credit packs</Text>
              <Text as="div" size="2" color="gray">
                {billing.aiCreditBalance} pack {billing.aiCreditBalance === 1 ? 'credit' : 'credits'} left.
                They do not expire, and they are used after the monthly credits run out.
              </Text>
            </Box>
            {billing.billingEnabled && billing.canManage && billing.creditPack.purchasable && (
              <Button
                variant="soft"
                onClick={() => setCheckoutDialog({
                  title: `Buy ${billing.creditPack.credits} AI credits`,
                  load: startCreditPackCheckout,
                })}
              >
                Buy {billing.creditPack.credits} credits for ${billing.creditPack.priceUsd}
              </Button>
            )}
          </Flex>
          {billing.hasBillingAccount && billing.canManage && billing.billingEnabled && (
            <Flex>
              <Button variant="soft" disabled={busy !== null} onClick={() => go('portal', openPortal)}>
                {busy === 'portal' ? <Spinner /> : null} Manage billing
              </Button>
            </Flex>
          )}
        </Flex>
      </Card>

      <Grid columns={{ initial: '1', md: String(billing.plans.length) }} gap="4">
        {billing.plans.map((p: PlanSummary) => {
          const current = p.key === billing.plan.key;
          const canBuy = billing.billingEnabled && billing.canManage && p.purchasable && !current;
          return (
            <Card key={p.key}>
              <Flex direction="column" gap="2" height="100%">
                <Flex justify="between" align="center">
                  <Heading size="4">{p.name}</Heading>
                  {current && <Badge color="green">Current</Badge>}
                </Flex>
                <Text size="5" weight="bold">
                  {p.priceMonthlyUsd === 0 ? 'Free' : `$${p.priceMonthlyUsd}/mo`}
                </Text>
                <Text size="2">{limitText(p.limits.venues, p.limits.venues === 1 ? 'venue' : 'venues')}</Text>
                <Text size="2">
                  {p.limits.gameNightsPerMonth === null
                    ? 'Unlimited game nights'
                    : `${p.limits.gameNightsPerMonth} game nights a month`}
                </Text>
                <Text size="2">
                  {p.limits.aiCreditsPerMonth === null
                    ? 'Unlimited AI credits'
                    : `${p.limits.aiCreditsPerMonth} AI credits a month`}
                </Text>
                {!p.features.includes('customBranding') && (
                  <Text size="2" color="gray">Shows a &ldquo;Powered by&rdquo; mark</Text>
                )}
                {p.features.map((f) => (
                  <Text key={f} size="2" color="gray">{FEATURE_LABELS[f] ?? f}</Text>
                ))}
                <Box mt="auto" pt="2">
                  {canBuy && (
                    billing.hasSubscription ? (
                      <Button variant="soft" disabled={busy !== null} onClick={() => go('portal', openPortal)}>
                        Switch in billing portal
                      </Button>
                    ) : (
                      <Button
                        disabled={busy !== null}
                        onClick={() => setCheckoutDialog({
                          title: `Upgrade to ${p.name}`,
                          load: () => startCheckout(p.key),
                        })}
                      >
                        Upgrade to {p.name}
                      </Button>
                    )
                  )}
                </Box>
              </Flex>
            </Card>
          );
        })}
      </Grid>
      <EmbeddedCheckout
        title={checkoutDialog?.title ?? ''}
        load={checkoutDialog?.load ?? null}
        onClose={() => setCheckoutDialog(null)}
      />
      <Text size="1" color="gray">
        Billed by {PRODUCT_BRAND.companyLegalName}. Charges appear as {PRODUCT_BRAND.statementDescriptor}.
      </Text>
    </Flex>
  );
};

export default BillingPage;
